import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, message, Spin, Radio, Select, InputNumber } from 'antd'; // Added Radio, Select, InputNumber
import moment from 'moment';
import flowsheetService from '../services/flowsheetService';
import { lifestyleCategories, getAllLifestyleParameters } from '../utils/lifestyleCategories'; // Import lifestyle definitions
import MomentDatePicker from './MomentDatePicker';

const { Option } = Select;

const EditFlowsheetEntryForm = ({ visible, onCancel, onOk, entry, patientId }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentEntryType, setCurrentEntryType] = useState('');
  const [currentEntryName, setCurrentEntryName] = useState('');
  const [parameterDefinition, setParameterDefinition] = useState(null);


  useEffect(() => {
    if (entry) {
      const entryType = entry.type || '';
      const entryName = entry.name || '';
      setCurrentEntryType(entryType);
      setCurrentEntryName(entryName);

      let paramDef = null;
      if (entryType === 'BooleanCheckboxOption' || entryType === 'Questionnaire' || entryType === 'BooleanCheckbox') {
        const allLifestyleParams = getAllLifestyleParameters();
        if (entryType === 'BooleanCheckboxOption') {
          // For BooleanCheckboxOption, entry.name is the option's key. We need to find its parent group.
          for (const lc of lifestyleCategories) {
            for (const p of lc.parameters) {
              if (p.type === 'BooleanCheckbox' && p.options && Array.isArray(p.options)) {
                if (p.options.some(opt => opt.key === entryName)) {
                  paramDef = p; // The parent group definition
                  // We also need to know this specific option is being edited, but the value is just true/false
                  // So, the paramDef for rendering is the parent, but the value is for the option.
                  break;
                }
              }
            }
            if (paramDef) break;
          }
        } else { // For Questionnaire or single BooleanCheckbox
          paramDef = allLifestyleParams.find(p => p.key === entryName && p.type === entryType);
        }
      }
      setParameterDefinition(paramDef);
      
      let initialValue = entry.value;
      if (entryType === 'BooleanCheckboxOption' || (paramDef && paramDef.type === 'BooleanCheckbox')) {
        initialValue = typeof entry.value === 'boolean' ? entry.value : String(entry.value).toLowerCase() === 'true';
      } else if (paramDef && paramDef.type === 'Questionnaire' && paramDef.options) {
        // Ensure the value matches one of the option values if it's a select
        const optionExists = paramDef.options.some(opt => opt.value === entry.value);
        initialValue = optionExists ? entry.value : undefined; // Set to undefined if not in options to avoid Select warning
      } else if (typeof entry.value === 'object' && entry.value !== null) {
        initialValue = JSON.stringify(entry.value, null, 2);
      }

      form.setFieldsValue({
        entryDate: entry.date ? moment(entry.date) : null,
        value: initialValue,
      });
    } else {
      form.resetFields();
      setCurrentEntryType('');
      setCurrentEntryName('');
      setParameterDefinition(null);
    }
  }, [entry, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        entryDate: values.entryDate.toISOString(),
        // Attempt to parse back to object if it was stringified, otherwise send as is.
        // This part might need refinement based on how 'value' vs 'structuredValue' is handled for different entry types.
        // The backend controller expects 'value' and/or 'structuredValue'.
        // We are sending 'value'. If the original entry.value was an object,
        // and we stringified it, the backend might need to handle this.
        // Or, the backend should primarily expect updates to the 'value' field for simple edits,
        // and 'structuredValue' for more complex ones.
        // For now, we send what's in the form's 'value' field.
        value: values.value,
      };
      
      // If the original entry had a structuredValue and the 'value' field in the form
      // is meant to represent that (e.g. for PE summaries, value is HTML, structuredValue is the object),
      // then the payload might need to differentiate.
      // Given the plan is to edit 'value' (primary data content), this should be okay.
      // If 'value' was stringified JSON for an object, it's sent as a string.
      // If 'value' was simple text/number, it's sent as such.

      await flowsheetService.editFlowsheetEntry(patientId, entry.entryId, payload);
      message.success('Flowsheet entry updated successfully!');
      onOk(); // Refresh data in parent
    } catch (error) {
      console.error('Failed to update flowsheet entry:', error);
      message.error(error.message || 'Failed to update flowsheet entry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderValueInput = () => {
    if (currentEntryType === 'BooleanCheckboxOption' || (parameterDefinition && parameterDefinition.type === 'BooleanCheckbox')) {
      // This handles both individual BooleanCheckboxOption entries (like caffeine_stimulants)
      // and direct BooleanCheckbox entries if their definition is found.
      return (
        <Radio.Group>
          <Radio value={true}>True</Radio>
          <Radio value={false}>False</Radio>
        </Radio.Group>
      );
    }

    if (parameterDefinition && parameterDefinition.type === 'Questionnaire' && parameterDefinition.options) {
      return (
        <Select placeholder="Select an option" style={{ width: '100%' }} allowClear>
          {parameterDefinition.options.map(opt => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      );
    }

    if (currentEntryType === 'Vital' || currentEntryType === 'Lab') {
        // Check if value is parseable as a number, otherwise use text input
        const isNumeric = entry && entry.value !== null && entry.value !== undefined && !isNaN(parseFloat(entry.value));
        if (isNumeric) {
            return <InputNumber style={{ width: '100%' }} placeholder="Enter numeric value" />;
        }
    }
    
    // Default to TextArea for text, HTML, JSON strings, or unhandled types
    return <Input.TextArea rows={4} placeholder="Enter value" />;
  };

  return (
    <Modal
      title={`Edit Flowsheet Entry: ${entry?.name || entry?.type || 'Entry'}`}
      visible={visible}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleOk}>
          Save Changes
        </Button>,
      ]}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" name="editFlowsheetEntryForm">
          <Form.Item
            name="entryDate"
            label="Entry Date/Time"
            rules={[{ required: true, message: 'Please select the entry date and time!' }]}
          >
            <MomentDatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="value"
            label={`Value ${currentEntryType ? `(${currentEntryType} - ${entry?.name || ''})` : ''}`}
            rules={[{ required: true, message: 'Please enter the value!' }]}
          >
            {renderValueInput()}
          </Form.Item>
          {/* Consider adding a field for 'structuredValue' if direct JSON editing is needed for some types */}
          {/* For example:
            entry && typeof entry.structuredValue === 'object' && (
            <Form.Item name="structuredValue" label="Structured Value (JSON)">
              <Input.TextArea rows={6} placeholder="Enter structured value as JSON" />
            </Form.Item>
          )*/}
        </Form>
      </Spin>
    </Modal>
  );
};

export default EditFlowsheetEntryForm;
